/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { useAncestorPath } from '@squiz/dxp-console-support';
import {
  ALERT_TYPE_WARNING,
  Alert,
  AlertProps,
  Breadcrumbs,
  BreadcrumbsProps,
  Loader,
  SPINNER_TYPE_REGULAR,
} from '@squiz/sds';
import { DxpScreen } from 'apps/console-core/src/Components/DxpScreen/DxpScreen';
import { useCapabilityBreadcrumbs } from 'apps/console-core/src/Hooks/UseCapabilityBreadcrumbs/UseCapabilityBreadcrumbs';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { lastValueFrom } from 'rxjs';
import { JsonObject } from 'type-fest';
import { COMPONENT_SERVICE_STRINGS } from '../../Constants/Strings.constants';
import {
  ComponentManifestResult,
  ComponentServiceGetComponentDto,
  ComponentSetDetailsResult,
  ComponentVersionResult,
  getComponentService,
} from '../../Services/component';
import { isSetNamespaceRestricted } from '../../Utilities/IsSetNamespaceRestricted/IsSetNamespaceRestricted';
import { ComponentDetailsDescription } from '../ComponentDetails/elements/ComponentDetailsDescription/ComponentDetailsDescription';
import { ComponentDetailsPreview } from '../ComponentDetails/elements/ComponentDetailsPreview/ComponentDetailsPreview';
import { ComponentDetailsWithinSetProps } from './ComponentDetailsWithinSet.props';
import { ComponentDetailsVersionsSetManagement } from './elements/ComponentDetailsVersionsSetManagement/ComponentDetailsVersionsSetManagement';
import { RemoveComponentFromSet } from './elements/RemoveComponentFromSet/RemoveComponentFromSet';

/**
 * The component details with component set context screen.
 */
export const ComponentDetailsWithinSet = (): JSX.Element => {
  const { t } = useTranslation();

  const [selectedComponentVersionData, setSelectedComponentVersionData] = useState<ComponentVersionResult>([
    null,
    null,
  ]);
  const [selectedComponentManifestVersion, setSelectedComponentManifestVersion] = useState<ComponentManifestResult>([
    null,
    null,
  ]);
  const [componentSetData, setComponentSetData] = useState<ComponentSetDetailsResult>([null, null]);
  const [selectedVersion, setSelectedVersion] = useState<string>(``);
  const [editableVersionsOnSet, setEditableVersionsOnSet] = useState<Array<string>>([]);
  const [renderableVersionsOnSet, setRenderableVersionsOnSet] = useState<Array<string>>([]);
  const [excludedVersionsOnSet, setExcludedVersionsOnSet] = useState<Array<string>>([]);
  const [noComponentVersionsAvailableOnSet, setNoComponentVersionsAvailableOnSet] = useState<boolean>(false);

  const params = useParams();
  const { componentNamespace, componentName, setWebpath } = params;
  const isSquizBaseComponent = isSetNamespaceRestricted(setWebpath || '');

  // Component service
  const componentService = getComponentService();

  const componentKey = useMemo(() => {
    return `${componentNamespace}/${componentName}`;
  }, [componentName, componentNamespace]);

  // Labels
  const labels = useMemo(() => {
    return {
      cancel: t(COMPONENT_SERVICE_STRINGS.cancel),
      componentSets: t(COMPONENT_SERVICE_STRINGS.componentSets),
      deleteError: t(COMPONENT_SERVICE_STRINGS.deleteError),
      remove: t(COMPONENT_SERVICE_STRINGS.remove),
      removeComponentFromSet: t(COMPONENT_SERVICE_STRINGS.removeComponentFromSet),
      removeComponentFromSetDescription: t(COMPONENT_SERVICE_STRINGS.removeComponentFromSetDescription),
      squizEditWarning: t(COMPONENT_SERVICE_STRINGS.squizEditWarning),
    };
  }, [t]);

  const updateToSelectedComponentVersion = useCallback((): void => {
    if (componentNamespace && componentName && selectedVersion) {
      // Construct request dto
      const dto: ComponentServiceGetComponentDto = {
        version: selectedVersion,
        webpath: `${componentNamespace}/${componentName}`,
      };

      // Send the request to get the component version details
      lastValueFrom(componentService.getComponentVersion(dto))
        .then((res) => {
          return setSelectedComponentVersionData([res, null]);
        })
        .catch((err) => {
          setSelectedComponentVersionData([null, null]);
        });

      // Send the request to get the latest component version manifest
      lastValueFrom(
        componentService.getComponentManifest({
          name: componentName,
          namespace: componentNamespace,
          version: selectedVersion,
        }),
      )
        .then((res) => {
          return setSelectedComponentManifestVersion([res, null]);
        })
        .catch((err) => {
          setSelectedComponentManifestVersion([null, null]);
        });
    }
  }, [componentName, componentNamespace, componentService, selectedVersion]);

  const updateComponentSetData = useCallback((): void => {
    lastValueFrom(componentService.getComponentSetDetails({ webpath: setWebpath }))
      .then((res) => {
        setComponentSetData([res, res as unknown as JsonObject]);
        return true;
      })
      .catch((err) => {
        setComponentSetData([null, null]);
      });
  }, [componentService, setWebpath]);

  useEffect(() => {
    updateComponentSetData && updateComponentSetData();
  }, [updateComponentSetData]);

  // Run every time the component version selection is updated
  useEffect(() => {
    updateToSelectedComponentVersion && updateToSelectedComponentVersion();
  }, [selectedVersion, updateToSelectedComponentVersion]);

  // The latest editable version is most relevant otherwise its the highest renderable version
  useEffect(() => {
    if (editableVersionsOnSet.length > 0) {
      return setSelectedVersion(editableVersionsOnSet[0]);
    } else if (renderableVersionsOnSet.length > 0) {
      return setSelectedVersion(renderableVersionsOnSet[0]);
    } else {
      return setSelectedVersion(``);
    }
  }, [editableVersionsOnSet, renderableVersionsOnSet]);

  // Update the states of Editable, Renderable, Excluded versions.
  useEffect(() => {
    const createEditableVersions = (): void => {
      if (componentSetData && componentSetData[0]) {
        const versions: Array<string> = [];
        if (componentSetData[0].components[componentKey] === undefined) {
          setEditableVersionsOnSet([]);
          return;
        }
        Object.values(componentSetData[0].components[componentKey]).forEach((componentObject) => {
          if (componentObject.editable) {
            versions.push(componentObject.version);
          }
        });
        setEditableVersionsOnSet(versions);
      }
    };
    createEditableVersions();

    const createRenderableVersions = (): void => {
      if (componentSetData && componentSetData[0]) {
        const versions: Array<string> = [];
        if (componentSetData[0].components[componentKey] === undefined) {
          setRenderableVersionsOnSet([]);
          return;
        }
        Object.values(componentSetData[0].components[componentKey]).forEach((componentObject) => {
          versions.push(componentObject.version);
        });
        setRenderableVersionsOnSet(versions);
      }
    };

    const createExcludedVersions = (): void => {
      if (componentSetData && componentSetData[0]) {
        if (componentSetData[0].componentVersionRules[componentKey] === undefined) {
          setExcludedVersionsOnSet([]);
          return;
        }
        const versions = componentSetData[0].componentVersionRules[componentKey].excludedVersions;
        setExcludedVersionsOnSet(versions);
      }
    };

    // Showing an hiding the preview section
    const noVersionsAvailableOnSet = (): void => {
      if (
        editableVersionsOnSet.length === 0 &&
        renderableVersionsOnSet.length === 0 &&
        excludedVersionsOnSet.length === 0
      ) {
        setNoComponentVersionsAvailableOnSet(true);
      }
      setNoComponentVersionsAvailableOnSet(false);
    };

    createEditableVersions();
    createRenderableVersions();
    createExcludedVersions();
    noVersionsAvailableOnSet();
  }, [
    componentKey,
    componentSetData,
    editableVersionsOnSet.length,
    excludedVersionsOnSet.length,
    renderableVersionsOnSet.length,
  ]);

  // Breadcrumbs
  const capabilityBreadcrumbs = useCapabilityBreadcrumbs();
  const breadcrumbProps: BreadcrumbsProps = {
    currentPageName: componentName,
    links: [
      ...capabilityBreadcrumbs,
      {
        name: labels.componentSets,
        url: useAncestorPath(true, 3),
      },
      {
        name: setWebpath as string,
        url: useAncestorPath(true, 2),
      },
    ],
  };

  // Create the breadcrumbs for this page
  const breadcrumbs = <Breadcrumbs {...breadcrumbProps} />;
  // Render the screen content
  let screenHeading = '';
  let screenContent = undefined;

  if (selectedComponentVersionData[0] && componentSetData[0]) {
    screenHeading = selectedComponentVersionData[0].displayName;
    const componentType = selectedComponentManifestVersion[0]?.type ?? 'server';

    const customProps: ComponentDetailsWithinSetProps = {
      componentDetails: selectedComponentVersionData[0],
      componentKey: componentKey,
      componentManifest: selectedComponentManifestVersion[0],
      componentSetProps: componentSetData[0],
      componentType: componentType as 'server' | 'edge',
      disabled: isSquizBaseComponent,
      editableVersions: editableVersionsOnSet,
      excludedVersions: excludedVersionsOnSet,
      name: componentName,
      namespace: componentNamespace,
      renderableVersions: renderableVersionsOnSet,
      selectedVersion: selectedVersion,
      updateComponentSetDetails: updateComponentSetData,
      updateVersion: (version: string) => {
        setSelectedVersion(version);
      },
    };

    const alertProps: AlertProps = {
      message: labels.squizEditWarning,
      type: ALERT_TYPE_WARNING,
    };

    screenContent = (
      <>
        {isSquizBaseComponent && <Alert {...alertProps} classes="component-details-within-set-alert" />}
        <div className="component-details-within-set__component-details-container">
          <ComponentDetailsDescription {...customProps} componentVersions={[]} />
          <div className="component-details-within-set__component-details-remove-button">
            <RemoveComponentFromSet
              componentKey={componentKey}
              disabled={isSquizBaseComponent}
              setWebpath={setWebpath as string}
            />
          </div>
        </div>

        <ComponentDetailsVersionsSetManagement {...customProps} />
        {noComponentVersionsAvailableOnSet ? (
          <></>
        ) : (
          <ComponentDetailsPreview {...customProps} componentVersions={[]} />
        )}
      </>
    );
  }

  return (
    <DxpScreen breadcrumbs={breadcrumbs} heading={screenHeading}>
      {screenContent !== undefined ? screenContent : <Loader type={SPINNER_TYPE_REGULAR} />}
    </DxpScreen>
  );
};
